import {
  Box,
  CollapseAllButton,
  Dialog,
  ExpandAllButton,
  FormControlSizes,
  HStack,
  NotificationVariants,
  Toggle,
  useCallbackRef,
  useEndpointsContext,
  useGlobalToasts,
  type CareOrder,
  type useDisclosureWithContext,
} from '@talos/kyoko';
import { useMemo, useState } from 'react';
import {
  CareOrderManualFillsBlotter,
  type CareOrderManualFillsBlotterExpansionControls,
} from './CareOrderManualFillsBlotter';
import { canManualFillCareOrders, manualFillCareOrdersSliceActions } from './manualFillCareOrdersSlice';
import { manuallyFillCareOrders } from './manuallyFillCareOrders';
import { useManualFillCareOrdersState } from './useManualFillCareOrdersState';

export interface CareOrderManualFillDialogContext {
  selectedCareOrders: CareOrder[];
}

interface CareOrderManualFillDialogProps {
  disclosure: ReturnType<typeof useDisclosureWithContext<CareOrderManualFillDialogContext>>;
}

export function CareOrderManualFillDialog({ disclosure }: CareOrderManualFillDialogProps) {
  if (!disclosure.context) {
    return null;
  }

  return <CareOrderManualFillDialogContent disclosure={disclosure} key={disclosure.openId} />;
}

function CareOrderManualFillDialogContent({ disclosure }: CareOrderManualFillDialogProps) {
  const { orgApiEndpoint } = useEndpointsContext();
  const { add: addToast } = useGlobalToasts();
  const { state, dispatch } = useManualFillCareOrdersState(disclosure.context?.selectedCareOrders ?? []);
  const [loading, setLoading] = useState(false);
  const [showAllDecimals, setShowAllDecimals] = useState(true);

  // The blotter throws up the expansion controls for us so we can render the expand all, collapse all buttons in the popper header
  const [expansionControls, setExpansionControls] = useState<CareOrderManualFillsBlotterExpansionControls>();

  const handleEntryFillQtyChange = useCallbackRef(
    (careOrderID: string, value: string | undefined, legSymbol?: string) => {
      dispatch(manualFillCareOrdersSliceActions.setEntryFillQty({ careOrderID, value, legSymbol }));
    }
  );
  const handleEntryFillPriceChange = useCallbackRef(
    (careOrderID: string, value: string | undefined, legSymbol?: string) => {
      dispatch(manualFillCareOrdersSliceActions.setEntryFillPrice({ careOrderID, value, legSymbol }));
    }
  );
  const handleEntryFillPriceReset = useCallbackRef((careOrderID: string, legSymbol?: string) => {
    dispatch(manualFillCareOrdersSliceActions.resetEntryFillPriceToDerived({ careOrderID, legSymbol }));
  });
  const handleEntryFillAmountChange = useCallbackRef(
    (careOrderID: string, value: string | undefined, legSymbol?: string) => {
      dispatch(manualFillCareOrdersSliceActions.setEntryFillAmount({ careOrderID, value, legSymbol }));
    }
  );
  const handleEntryFillAmountReset = useCallbackRef((careOrderID: string, legSymbol?: string) => {
    dispatch(manualFillCareOrdersSliceActions.resetEntryFillAmountToDerived({ careOrderID, legSymbol }));
  });
  const handleEntryRemove = useCallbackRef((careOrderID: string) => {
    dispatch(manualFillCareOrdersSliceActions.removeEntries({ careOrderIDs: [careOrderID] }));
  });

  const canManualFillCareOrdersResult = useMemo(() => canManualFillCareOrders(state), [state]);
  const entriesCount = state.entries.length;

  const handleConfirm = useCallbackRef(async () => {
    if (!orgApiEndpoint) {
      addToast({
        text: 'Could not manually fill care orders: missing organization API endpoint.',
        variant: NotificationVariants.Negative,
      });
      return;
    }

    setLoading(true);
    try {
      const { allSucceeded, successfulCareOrderIDs } = await manuallyFillCareOrders({
        entries: state.entries,
        orgApiEndpoint,
        addToast,
      });
      dispatch(manualFillCareOrdersSliceActions.removeEntries({ careOrderIDs: successfulCareOrderIDs }));

      if (allSucceeded) {
        disclosure.close();
      }
    } finally {
      setLoading(false);
    }
  });

  return (
    <Dialog
      {...disclosure}
      contentPadding={0}
      title="Manually Fill Care Orders"
      showClose
      headerSuffix={
        <HStack gap="spacing8">
          <ExpandAllButton
            disabled={!expansionControls}
            onClick={() => expansionControls?.expandAllGroups()}
            data-testid="care-order-manual-fill-expand-all-button"
          />
          <CollapseAllButton
            disabled={!expansionControls}
            onClick={() => expansionControls?.collapseAllGroups()}
            data-testid="care-order-manual-fill-collapse-all-button"
          />
          <Toggle
            size={FormControlSizes.Small}
            checked={showAllDecimals}
            onChange={setShowAllDecimals}
            label="Show All Decimals"
            data-testid="care-order-manual-fill-show-all-decimals-toggle"
          />
        </HStack>
      }
      confirmLabel={`Submit ${entriesCount} fill${entriesCount === 1 ? '' : 's'}`}
      cancelLabel="Close"
      onConfirm={handleConfirm}
      confirmDisabled={loading || !canManualFillCareOrdersResult.valid}
      confirmLoading={loading}
      confirmButtonTooltip={
        canManualFillCareOrdersResult.valid === false ? canManualFillCareOrdersResult.reason : undefined
      }
      onConfirmMouseOver={() => dispatch(manualFillCareOrdersSliceActions.touchAll())}
      width="min(1400px, 90vw)"
      dataTestId="care-order-manual-fill-dialog"
    >
      <Box w="100%" h="550px">
        <CareOrderManualFillsBlotter
          entries={state.entries}
          onEntryFillQtyChange={handleEntryFillQtyChange}
          onEntryFillPriceChange={handleEntryFillPriceChange}
          onEntryFillPriceReset={handleEntryFillPriceReset}
          onEntryFillAmountChange={handleEntryFillAmountChange}
          onEntryFillAmountReset={handleEntryFillAmountReset}
          onEntryRemove={handleEntryRemove}
          onExpansionControlsChange={setExpansionControls}
          showAllDecimals={showAllDecimals}
          isLoading={loading}
        />
      </Box>
    </Dialog>
  );
}
